import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  getCurrentUserToken,
  onAuthStateChange,
  signOut as firebaseSignOut,
  extractClaimFromToken,
  signInWithCustomTokenAndGetJWT,
} from '../config/firebase';
import { AUTH_REJECTED, generateOTP, getClientProfile, onAuthRejected, verifyOTP } from '../services/trustline';

interface AuthContextType {
  email: string;
  clientId: string | null;
  firebaseToken: string | null;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  isGeneratingOTP: boolean;
  isVerifyingOTP: boolean;
  otpSent: boolean;
  authError: string | null;
  /** The backend refused the saved session: the user signs in again (the email is kept). */
  sessionExpired: boolean;
  setEmail: (email: string) => void;
  sendOTP: (email: string) => Promise<boolean>;
  verifyOTPCode: (otp: string) => Promise<boolean>;
  resendOTP: () => void;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const loadPersistedEmail = (): string => {
    try {
      return localStorage.getItem('trustlineEmail') || '';
    } catch {
      return '';
    }
  };

  const [email, setEmailState] = useState(loadPersistedEmail);
  const [clientId, setClientId] = useState<string | null>(null);
  const [firebaseToken, setFirebaseToken] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isGeneratingOTP, setIsGeneratingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // When the last sign-in happened: a token refused right after it is not an expired session.
  const lastSignInRef = React.useRef(0);

  /** Drop a session the backend no longer accepts, keeping the email for a new code. */
  const expireSession = useCallback(async () => {
    try {
      await firebaseSignOut();
    } catch {
      /* already signed out */
    }
    setFirebaseToken(null);
    setClientId(null);
    if (Date.now() - lastSignInRef.current < 120_000) {
      setAuthError('Trustline refused the new session. Please try again in a moment, or contact us if it persists.');
    } else {
      setSessionExpired(true);
    }
  }, []);

  useEffect(() => onAuthRejected(() => void expireSession()), [expireSession]);

  const setEmail = useCallback((value: string) => {
    setEmailState(value);
    if (value) {
      localStorage.setItem('trustlineEmail', value);
    } else {
      localStorage.removeItem('trustlineEmail');
    }
  }, []);

  useEffect(() => {
    // Fires once at start with the saved session (or none), then on every sign-in and sign-out.
    // A saved session is checked with the backend before the dashboard opens: if it is refused,
    // the user lands on the sign-in page, not on a page full of errors.
    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        try {
          const token = await getCurrentUserToken();
          if (token) {
            const check = await getClientProfile();
            if (!check.success && AUTH_REJECTED.test(check.error || '')) {
              await expireSession();
            } else {
              setFirebaseToken(token);
              setClientId(extractClaimFromToken(token, 'clientId'));
            }
          }
        } catch (error) {
          console.error('Failed to get user token:', error);
        }
      } else {
        setFirebaseToken(null);
        setClientId(null);
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, [expireSession]);

  const sendOTP = useCallback(async (emailValue: string): Promise<boolean> => {
    setIsGeneratingOTP(true);
    setAuthError(null);
    setEmail(emailValue.trim());

    try {
      const result = await generateOTP(emailValue.trim());
      if (result.success && result.idToken) {
        setIdToken(result.idToken);
        setIsGeneratingOTP(false);
        return true;
      }
      setAuthError(result.error || 'Failed to generate OTP');
      setIsGeneratingOTP(false);
      return false;
    } catch (error: any) {
      setAuthError(error.message || 'An error occurred while generating OTP');
      setIsGeneratingOTP(false);
      return false;
    }
  }, [setEmail]);

  const verifyOTPCode = useCallback(async (otp: string): Promise<boolean> => {
    if (!idToken || !email) {
      setAuthError('Please generate an OTP first');
      return false;
    }

    setIsVerifyingOTP(true);
    setAuthError(null);

    try {
      const result = await verifyOTP(idToken, otp.trim(), email);
      if (result.success && result.firebaseToken) {
        lastSignInRef.current = Date.now();
        const jwtToken = await signInWithCustomTokenAndGetJWT(result.firebaseToken);
        const extractedClientId = extractClaimFromToken(jwtToken, 'clientId');
        setFirebaseToken(jwtToken);
        setClientId(extractedClientId);
        setIdToken(null);
        setSessionExpired(false);
        setIsVerifyingOTP(false);
        return true;
      }
      setAuthError(result.error || 'Failed to verify OTP');
      setIsVerifyingOTP(false);
      return false;
    } catch (error: any) {
      setAuthError(error.message || 'An error occurred while verifying OTP');
      setIsVerifyingOTP(false);
      return false;
    }
  }, [idToken, email]);

  const resendOTP = useCallback(() => {
    setIdToken(null);
    setAuthError(null);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut();
    } catch (error) {
      console.error('Error signing out from Firebase:', error);
    }
    setEmailState('');
    setClientId(null);
    setFirebaseToken(null);
    setIdToken(null);
    setAuthError(null);
    setSessionExpired(false);
    localStorage.removeItem('trustlineEmail');
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    return getCurrentUserToken();
  }, []);

  const isAuthenticated = !!(firebaseToken && email);

  return (
    <AuthContext.Provider
      value={{
        email,
        clientId,
        firebaseToken,
        isAuthenticated,
        isAuthReady,
        isGeneratingOTP,
        isVerifyingOTP,
        otpSent: !!idToken && !firebaseToken,
        authError,
        sessionExpired,
        setEmail,
        sendOTP,
        verifyOTPCode,
        resendOTP,
        signOut,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
